// src/components/shop/ShopHeroSlider.jsx
import React, {useState, useEffect, useRef} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import LuxuryButton from "../ui/LuxuryButton";
import {
    FaArrowRight, FaLeaf, FaFlask, FaGem, FaStar, FaPlay,
} from 'react-icons/fa';
import '../../styles/globals.css';
import demoImage from "../../assets/images/demo.png";

// Hero slides data
const heroSlides = [{
    id: 1,
    title: "Discover Your",
    subtitle: "Signature Ritual",
    description: "Experience transformative beauty crafted with precision and passion",
    stats: [{value: "24", label: "Active Ingredients"}, {value: "98%", label: "Natural Origin"}, {
        value: "5★",
        label: "Rated"
    }],
    ctaPrimary: "Explore Collection",
    ctaSecondary: "Our Story",
    badge: "New Launch",
    productName: "Luminous Elixir",
    productTagline: "Limited Edition",
    rating: "4.9 Rating",
    gradient: "linear-gradient(135deg, #f9f5f2 0%, #f0e9e4 100%)",
    accentColor: "#b08d7a",
    productColor: "#d4b483",
    productGradient: "linear-gradient(135deg, #d4b48320 0%, #9caf8820 100%)"
}, {
    id: 2,
    title: "Botanical",
    subtitle: "Intelligence",
    highlight: "Perfected",
    description: "Harnessing nature's most potent botanicals with scientific precision for transformative results",
    stats: [{value: "100%", label: "Cruelty Free"}, {value: "Vegan", label: "Formulas"}, {value: "5★", label: "Rated"}],
    ctaPrimary: "Discover Ingredients",
    ctaSecondary: "Learn Our Process",
    badge: "Limited Edition",
    productName: "Moonlight Elixir",
    productTagline: "Night Serum",
    rating: "4.8 Rating",
    gradient: "linear-gradient(135deg, #E8DFD0 0%, #D8C9B0 100%)",
    accentColor: "#9caf88",
    productColor: "#7A8F8C",
    productGradient: "linear-gradient(135deg, #9caf8820 0%, #7A8F8C20 100%)"
}, {
    id: 3,
    title: "Emotional",
    subtitle: "Wellness",
    highlight: "Ritualized",
    description: "Transform your skincare routine into a mindful ritual that nourishes both skin and spirit",
    stats: [{value: "48hr", label: "Hydration"}, {value: "3x", label: "More Effective"}, {value: "5★", label: "Rated"}],
    ctaPrimary: "Start Your Ritual",
    ctaSecondary: "Read Our Journal",
    badge: "Mindful Beauty",
    productName: "Serenity Blend",
    productTagline: "Ritual Oil",
    rating: "4.9 Rating",
    gradient: "linear-gradient(135deg, #F5F0E6 0%, #E8E0D1 100%)",
    accentColor: "#b08d7a",
    productColor: "#d4b483",
    productGradient: "linear-gradient(135deg, #d4b48320 0%, #E8E0D1 100%)"
}];

const ShopHeroSlider = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isPlaying] = useState(true);
    const [scrollProgress, setScrollProgress] = useState(0);
    const intervalRef = useRef(null);
    const containerRef = useRef(null);

    // Auto-rotate slides every 3 seconds
    useEffect(() => {
        if (!isPlaying) return;

        intervalRef.current = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 3000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isPlaying]);

    // Scroll progress for parallax effect
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset;
            const windowHeight = window.innerHeight;
            const progress = Math.min(scrollTop / (windowHeight * 0.5), 1);
            setScrollProgress(progress);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const currentSlideData = heroSlides[currentSlide];

    // Animation variants
    const slideVariants = {
        enter: (direction) => ({
            x: direction > 0 ? 1000 : -1000, opacity: 0
        }), center: {
            zIndex: 1, x: 0, opacity: 1
        }, exit: (direction) => ({
            zIndex: 0, x: direction < 0 ? 1000 : -1000, opacity: 0
        })
    };

    const contentVariants = {
        hidden: {opacity: 0, y: 30}, visible: {
            opacity: 1, y: 0, transition: {
                duration: 0.8, ease: "easeOut"
            }
        }
    };

    const imageVariants = {
        hidden: {
            opacity: 0, scale: 0.9, rotate: -5
        }, visible: {
            opacity: 1, scale: 1, rotate: 0, transition: {
                duration: 1, ease: "easeOut"
            }
        }
    };

    return (<section
            ref={containerRef}
            className="
    relative overflow-hidden
    min-h-screen
    pt-10
    sm:pt-12
    md:pt-16
    lg:min-h-[80vh]
    lg:pt-20
    xl:pt-24
  "
            style={{
                background: currentSlideData.gradient,
            }}
        >
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
                {/* Floating Particles */}
                {[...Array(12)].map((_, i) => (<motion.div
                        key={i}
                        className="absolute"
                        animate={{
                            y: [0, -30, 0], rotate: [0, 180, 360]
                        }}
                        transition={{
                            duration: 6 + i * 0.5, repeat: Infinity, ease: "easeInOut"
                        }}
                        style={{
                            left: `${(i * 8) % 100}%`, top: `${(i * 6) % 100}%`,
                        }}
                    >
                        {i % 3 === 0 ? (<FaLeaf className="w-4 h-4"
                                                style={{color: `${currentSlideData.accentColor}30`}}/>) : i % 3 === 1 ? (
                            <FaFlask className="w-4 h-4" style={{color: `${currentSlideData.accentColor}30`}}/>) : (
                            <FaStar className="w-4 h-4" style={{color: `${currentSlideData.accentColor}20`}}/>)}
                    </motion.div>))}

                {/* Gradient Orbs */}
                <motion.div
                    className="absolute top-1/4 -left-32 w-96 h-96 rounded-full"
                    style={{
                        background: `radial-gradient(circle, ${currentSlideData.productColor}15 0%, transparent 70%)`,
                        filter: 'blur(40px)',
                    }}
                    animate={{
                        scale: [1, 1.3, 1], x: [0, 40, 0], y: [0, -20, 0],
                    }}
                    transition={{
                        duration: 15, repeat: Infinity, ease: "easeInOut",
                    }}
                />

                <motion.div
                    className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full"
                    style={{
                        background: `radial-gradient(circle, ${currentSlideData.accentColor}10 0%, transparent 70%)`,
                        filter: 'blur(30px)',
                    }}
                    animate={{
                        scale: [1, 1.4, 1], x: [0, -30, 0], y: [0, 20, 0],
                    }}
                    transition={{
                        duration: 18, repeat: Infinity, ease: "easeInOut", delay: 1,
                    }}
                />

                {/* Grid Pattern */}
                <div
                    className="absolute inset-0 opacity-[0.02]"
                    style={{
                        backgroundImage: `
              linear-gradient(to right, #333 1px, transparent 1px),
              linear-gradient(to bottom, #333 1px, transparent 1px)
            `, backgroundSize: '50px 50px'
                    }}
                />
            </div>

            {/* Slide Content */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentSlide}
                    custom={1}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                        x: {type: "spring", stiffness: 300, damping: 30}, opacity: {duration: 0.4}
                    }}
                    className="container-luxury relative z-10 h-screen flex items-center"
                >
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center w-full">
                        {/* Left Column - Text Content */}
                        <div>
                            <motion.div
                                variants={contentVariants}
                                initial="hidden"
                                animate="visible"
                                className="space-y-6"
                            >
                                {/* Badges - Adjusted margins */}
                                <div className="mb-4">
                                    <div
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-champagne/20 to-sage/20 backdrop-blur-sm rounded-full border border-champagne/30">
                                        <FaGem className="w-3 h-3 text-champagne animate-pulse"/>
                                        <span className="text-xs tracking-widest uppercase text-champagne font-medium">
                                            {currentSlideData.badge}
                                        </span>
                                        <div className="w-1 h-1 bg-champagne rounded-full animate-ping"/>
                                    </div>
                                </div>

                                {/* Main Headline - Adjusted margins */}
                                <div className="space-y-3 mb-4">
                                    <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-heading font-light leading-[0.9]">
                                        {currentSlideData.title}
                                        <span className="block text-sage italic mt-2">
                                            {currentSlideData.subtitle}
                                        </span>
                                    </h1>
                                    <div className="w-24 h-px bg-gradient-to-r from-champagne to-transparent"/>
                                </div>

                                {/* Subtitle - Adjusted margins */}
                                <p className="text-2xl md:text-3xl text-charcoal/60 leading-relaxed mb-6">
                                    {currentSlideData.description}
                                </p>

                                {/* Stats - Adjusted margins */}
                                <div className="flex flex-wrap gap-6 mb-8">
                                    {currentSlideData.stats.map((stat, index) => (
                                        <div key={index} className="text-center">
                                            <div
                                                className="text-3xl font-light mb-1"
                                                style={{color: currentSlideData.accentColor}}
                                            >
                                                {stat.value}
                                            </div>
                                            <div className="text-sm text-charcoal/40 uppercase tracking-wider">
                                                {stat.label}
                                            </div>
                                        </div>))}
                                </div>

                                {/* CTA Buttons - Adjusted margins */}
                                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                                    <LuxuryButton
                                        variant="primary"
                                        size="large"
                                        onClick={() => {
                                            document.getElementById('products')?.scrollIntoView({
                                                behavior: 'smooth'
                                            });
                                        }}
                                        className="group"
                                        icon={FaArrowRight}
                                        iconPosition="right"
                                    >
                                        <span>{currentSlideData.ctaPrimary}</span>
                                    </LuxuryButton>

                                    <LuxuryButton
                                        variant="secondary"
                                        size="large"
                                        onClick={() => {
                                            console.log('Watch story');
                                        }}
                                        icon={FaPlay}
                                        iconPosition="left"
                                    >
                                        <span className="hidden sm:inline">{currentSlideData.ctaSecondary}</span>
                                        <span className="sm:hidden">Story</span>
                                    </LuxuryButton>
                                </div>

                                {/* Trust Indicators - Adjusted margins */}
                                <div className="flex items-center gap-3 text-sm text-charcoal/40">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-sage rounded-full"/>
                                        <span>Cruelty Free</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-champagne rounded-full"/>
                                        <span>Vegan Formula</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-charcoal/40 rounded-full"/>
                                        <span>Sustainable</span>
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        {/* Right Column - Product Visual */}
                        <motion.div
                            variants={imageVariants}
                            initial="hidden"
                            animate="visible"
                            className="relative hidden lg:block"
                        >
                            {/* Product Container */}
                            <div className="relative aspect-square rounded-3xl overflow-hidden">
                                <div
                                    className="absolute inset-0"
                                    style={{background: currentSlideData.productGradient}}
                                />

                                {/* Floating Product Mockup */}
                                <motion.div
                                    className="absolute inset-8 bg-gradient-to-br from-white to-pearl rounded-2xl shadow-lift"
                                    animate={{
                                        y: [0, -20, 0], rotate: [0, 1, -1, 0]
                                    }}
                                    transition={{
                                        duration: 8, repeat: Infinity, ease: "easeInOut"
                                    }}
                                >
                                    {/* Product Label */}
                                    <div className="absolute top-8 left-8 right-8">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="text-xs tracking-widest uppercase text-sage mb-1">
                                                    {currentSlideData.badge}
                                                </div>
                                                <div className="text-xl font-medium text-charcoal">
                                                    {currentSlideData.productName}
                                                </div>
                                            </div>
                                            <div
                                                className="w-10 h-10 rounded-full flex items-center justify-center"
                                                style={{
                                                    background: `linear-gradient(135deg, ${currentSlideData.productColor}, ${currentSlideData.accentColor})`
                                                }}
                                            >
                                                <FaStar className="w-4 h-4 text-white"/>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Product Preview */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <img
                                            src={demoImage}
                                            alt={currentSlideData.productName}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                maxWidth: "320px",
                                                maxHeight: "480px",
                                                objectFit: "contain"
                                            }}
                                            draggable={false}
                                        />
                                    </div>

                                    {/* Floating Particles */}
                                    {[...Array(8)].map((_, i) => (<motion.div
                                            key={i}
                                            className="absolute w-2 h-2 rounded-full"
                                            style={{
                                                background: `linear-gradient(to right, ${currentSlideData.productColor}40, ${currentSlideData.accentColor}40)`,
                                                left: `${20 + (i * 10)}%`,
                                                bottom: '20%'
                                            }}
                                            animate={{
                                                y: [0, -30, 0], x: [0, Math.sin(i) * 10, 0], opacity: [0.3, 0.8, 0.3]
                                            }}
                                            transition={{
                                                duration: 3 + i * 0.4, repeat: Infinity, ease: "easeInOut"
                                            }}
                                        />))}
                                </motion.div>

                                {/* Decorative Border */}
                                <motion.div
                                    className="absolute -inset-1 border-2 border-transparent rounded-3xl"
                                    animate={{
                                        borderColor: [`${currentSlideData.productColor}20`, `${currentSlideData.accentColor}40`, `${currentSlideData.productColor}20`]
                                    }}
                                    transition={{
                                        duration: 4, repeat: Infinity, ease: "easeInOut"
                                    }}
                                />
                            </div>

                            {/* Floating Badge */}
                            <motion.div
                                className="absolute -top-4 -right-4 px-6 py-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lift flex items-center gap-3"
                                animate={{
                                    scale: [1, 1.05, 1], rotate: [0, 2, -2, 0]
                                }}
                                transition={{
                                    duration: 4, repeat: Infinity, ease: "easeInOut"
                                }}
                            >
                                <div
                                    className="w-3 h-3 rounded-full animate-pulse"
                                    style={{backgroundColor: currentSlideData.productColor}}
                                />
                                <span className="text-sm font-medium text-charcoal">
                                    {currentSlideData.productTagline}
                                </span>
                            </motion.div>

                            {/* Rating Badge */}
                            <motion.div
                                className="absolute -bottom-4 left-8 px-4 py-2 text-white rounded-full flex items-center gap-2 shadow-lift"
                                style={{
                                    background: `linear-gradient(to right, ${currentSlideData.productColor}, ${currentSlideData.accentColor})`
                                }}
                                initial={{y: 20, opacity: 0}}
                                animate={{y: 0, opacity: 1}}
                                transition={{delay: 0.8}}
                            >
                                <FaStar className="w-3 h-3"/>
                                <span className="text-sm font-medium">{currentSlideData.rating}</span>
                            </motion.div>
                        </motion.div>
                    </div>
                </motion.div>
            </AnimatePresence>

            {/* Slide Indicators */}
            <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
                {heroSlides.map((_, index) => (<button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'w-8 bg-champagne' : 'bg-charcoal/30 hover:bg-charcoal/50'}`}
                        aria-label={`Go to slide ${index + 1}`}
                    />))}
            </div>

            {/* Scroll Indicator */}
            <motion.div
                className="absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer z-20"
                animate={{
                    y: [0, 10, 0], opacity: [0.3, 0.8, 0.3]
                }}
                transition={{
                    duration: 2, repeat: Infinity, ease: "easeInOut"
                }}
                onClick={() => {
                    document.getElementById('products')?.scrollIntoView({
                        behavior: 'smooth'
                    });
                }}
            >
                <div className="flex flex-col items-center gap-2">
                    <span className="text-xs tracking-widest uppercase text-charcoal/30">
                        Scroll to Explore
                    </span>
                    <div className="relative">
                        <div
                            className="w-px h-16 bg-gradient-to-b from-champagne via-sage to-transparent"
                            style={{
                                background: `linear-gradient(to bottom, ${currentSlideData.productColor}, ${currentSlideData.accentColor}, transparent)`
                            }}
                        />
                        <motion.div
                            className="absolute top-0 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full"
                            style={{backgroundColor: currentSlideData.productColor}}
                            animate={{y: [0, 60, 0]}}
                            transition={{
                                duration: 2, repeat: Infinity, ease: "easeInOut"
                            }}
                        />
                    </div>
                </div>
            </motion.div>

            {/* Parallax Text */}
            <motion.div
                className="absolute bottom-20 left-0 right-0 pointer-events-none overflow-hidden z-0"
                style={{y: scrollProgress * 100}}
            >
                <div className="container-luxury">
                    <div className="text-center">
                        <span className="text-6xl md:text-8xl font-heading font-bold text-white/20 tracking-wider">
                            KROMAIC
                        </span>
                    </div>
                </div>
            </motion.div>
        </section>);
};

export default ShopHeroSlider;